import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginForm from './components/Auth/LoginForm';
import EmployeePOSLogin from './components/Auth/EmployeePOSLogin';
import Dashboard from './components/Dashboard/Dashboard';
import * as api from './api';
import { createOrder } from './api';

const App = () => {
  // ===============================
  // ESTADO DE AUTENTICACIÓN
  // ===============================
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // 🔥 SESIÓN DE EMPLEADO POS
  const [employeeSession, setEmployeeSession] = useState(null);

  // ===============================
  // DATOS PRINCIPALES
  // ===============================
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [sales, setSales] = useState([]);
  const [supplyCategories, setSupplyCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inventory, setInventory] = useState([]);

  // ===============================
  // RESTAURAR SESIÓN
  // ===============================
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      parsed.access =
        typeof parsed.access === 'string'
          ? JSON.parse(parsed.access)
          : parsed.access;
      setCurrentUser(parsed);
      setIsAuthenticated(true);
    }

    // 🔥 Restaurar sesión de empleado si existe
    const storedEmployee = localStorage.getItem('employeeSession');
    if (storedEmployee) {
      setEmployeeSession(JSON.parse(storedEmployee));
    }
  }, []);

  // ===============================
  // SAFE WRAPPER PARA APIS
  // ===============================
  const safe = async (promise, name) => {
    try {
      return await promise;
    } catch (error) {
      console.warn(`API falló: ${name}`, error);
      return { success: false, data: [] };
    }
  };

  // ===============================
  // CARGA DE DATOS POST LOGIN
  // ===============================
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchAll = async () => {
      const [
        catsRes,
        menusRes,
        supRes,
        recRes,
        salesRes,
        supCatRes,
        empRes,
        inventoryRes
      ] = await Promise.all([
        safe(api.getCategories(), 'categories'),
        safe(api.getMenuItems(), 'menuItems'),
        safe(api.getSupplies(), 'supplies'),
        safe(api.getRecipes(), 'recipes'),
        safe(api.getSales(), 'sales'),
        safe(api.getSupplyCategories(), 'supplyCategories'),
        safe(api.getEmployees(), 'employees'),
        safe(api.getInventory(), 'inventory')
      ]);

      setCategories(catsRes.data || []);
      setMenuItems(menusRes.data || []);
      setSupplies(supRes.data || []);
      setRecipes(recRes.data || []);
      setSales(salesRes.data || []);
      setSupplyCategories(supCatRes.data || []);
      setEmployees(empRes.data || []);
      setInventory(inventoryRes.data || []);
    };

    fetchAll();
  }, [isAuthenticated]);

  // ===============================
  // LOGIN SISTEMA
  // ===============================
  const handleLogin = async ({ username, password }) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL || '/api'}/login.php`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      }
    );

    const data = await res.json();
    if (data.success) {
      const parsedUser = {
        ...data.data.user,
        access:
          typeof data.data.user.access === 'string'
            ? JSON.parse(data.data.user.access)
            : data.data.user.access
      };
      setIsAuthenticated(true);
      setCurrentUser(parsedUser);
      localStorage.setItem('user', JSON.stringify(parsedUser));
      return true;
    }
    return false;
  };

  // ===============================
  // LOGOUT SISTEMA
  // ===============================
  const handleLogout = () => {
    api.logout?.();
    localStorage.removeItem('user');
    localStorage.removeItem('employeeSession');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setEmployeeSession(null);
  };

  // ===============================
  // 🔥 LOGIN EMPLEADO POS
  // ===============================
  const handleEmployeeLogin = (employee) => {
    localStorage.setItem('employeeSession', JSON.stringify(employee));
    setEmployeeSession(employee);
  };

  // ===============================
  // 🔥 LOGOUT EMPLEADO POS
  // (vuelve a la pantalla de PIN, no cierra sesión del sistema)
  // ===============================
  const handleEmployeeLogout = () => {
    localStorage.removeItem('employeeSession');
    setEmployeeSession(null);
  };

  // ===============================
  // CRUD CATEGORÍAS
  // ===============================
  const handleAddCategory = async (category) => {
    const res = await api.createCategory(category);
    if (res.success) setCategories(prev => [...prev, res.data]);
  };

  const handleDeleteCategory = async (id) => {
    const res = await api.deleteCategory(id);
    if (res.success)
      setCategories(prev => prev.filter(c => c.id !== id));
  };

  // ===============================
  // CRUD MENU ITEMS
  // ===============================
  // FIX: devolver id del producto creado para que MenuItemForm guarde las variantes
const handleAddMenuItem = async (item) => {
  const res = await fetch(
    `${process.env.REACT_APP_API_BASE_URL || '/api'}/create_menu_item.php`,
    { method: "POST", body: item }
  );
  const data = await res.json();
  console.log("CREATE MENU ITEM:", data);
  if (data.success) {
    const menusRes = await safe(api.getMenuItems(), 'menuItems-refresh');
    setMenuItems(menusRes.data || []);
    // FIX: devolver el id para que MenuItemForm pueda guardar variantes
    return data.data?.id ?? null;
  } else {
    console.error("ERROR:", data.error);
    return null;
  }
};

  const handleUpdateMenuItem = async (item) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL || '/api'}/update_product.php`,
      { method: "POST", body: item }
    );
    const data = await res.json();
    console.log("UPDATE MENU ITEM:", data);
    if (data.success) {
      const menusRes = await safe(api.getMenuItems(), 'menuItems-refresh');
      setMenuItems(menusRes.data || []);
    } else {
      console.error("ERROR:", data.error);
    }
  };

  const handleDeleteMenuItem = async (id) => {
    const res = await api.deleteMenuItem(id);
    if (res.success)
      setMenuItems(prev => prev.filter(i => i.id !== id));
  };

  // ===============================
  // CRUD SUPPLIES
  // ===============================
  const handleAddSupply = async (supply) => {
    const res = await api.createSupply(supply);
    if (res.success) setSupplies(prev => [...prev, res.data]);
  };

  const handleUpdateSupply = async (supply) => {
    const res = await api.updateSupply(supply);
    if (res.success)
      setSupplies(prev => prev.map(s => (s.id === supply.id ? res.data : s)));
  };

  const handleDeleteSupply = async (id) => {
    const res = await api.deleteSupply(id);
    if (res.success)
      setSupplies(prev => prev.filter(s => s.id !== id));
  };

  // ===============================
  // CRUD CATEGORÍAS DE INSUMOS
  // ===============================
  const handleAddSupplyCategory = async (cat) => {
    const res = await api.createSupplyCategory(cat);
    if (res.success) setSupplyCategories(prev => [...prev, res.data]);
  };

  const handleDeleteSupplyCategory = async (id) => {
    const res = await api.deleteSupplyCategory(id);
    if (res.success) {
      setSupplyCategories(prev => prev.filter(c => c.id !== id));
      setSupplies(prev => prev.filter(s => s.categoryId !== id));
    }
  };

  // ===============================
  // CRUD RECETAS
  // ===============================
  const handleSaveRecipe = async (recipe) => {
    const res = await api.createRecipe(recipe);
    if (res.success) setRecipes(prev => [...prev, res.data]);
  };

  const handleUpdateRecipe = async (recipe) => {
    const res = await api.updateRecipe(recipe);
    if (res.success)
      setRecipes(prev =>
        prev.map(r => (r.id === recipe.id ? res.data : r))
      );
  };

  const handleDeleteRecipe = async (id) => {
    const res = await api.deleteRecipe(id);
    if (res.success) setRecipes(prev => prev.filter(r => r.id !== id));
  };

  // ===============================
  // CRUD EMPLEADOS
  // ===============================
  const handleAddEmployee = async (emp) => {
    const res = await api.createEmployee(emp);
    if (res.success) setEmployees(prev => [...prev, res.data]);
  };

  const handleUpdateEmployee = async (emp) => {
    const res = await api.updateEmployee(emp);
    if (res.success)
      setEmployees(prev => prev.map(e => (e.id === emp.id ? res.data : e)));
  };

  const handleDeleteEmployee = async (id) => {
    const res = await api.deleteEmployee(id);
    if (res.success) setEmployees(prev => prev.filter(e => e.id !== id));
  };

  // ===============================
  // POS → CREAR ORDEN
  // ===============================
 // FIX: onAddPOSSale desde MenuPOS se llama sin args solo para refrescar
const handleAddPOSSale = async (payload) => {
  try {
    // FIX: si no hay payload, solo refrescar ventas
    if (!payload) {
      const salesRes = await safe(api.getSales(), 'sales-refresh');
      setSales(salesRes.data || []);
      return;
    }

    const res = await createOrder(payload);
    if (!res.success) {
      alert(res.error || "Error creando la orden");
      return res;
    }
    console.log("Orden creada:", res);
    const salesRes = await safe(api.getSales(), 'sales-refresh');
    setSales(salesRes.data || []);
    return res;
  } catch (error) {
    console.error("Error creando orden:", error);
    return { success: false };
  }
};

  // ===============================
  // ACTUALIZAR ESTADO DE VENTA
  // ===============================
  const handleUpdateSaleStatus = async (id, status) => {
    const res = await api.updateSaleStatus(id, status);
    if (res.success) {
      const salesRes = await safe(api.getSales(), 'sales-refresh');
      setSales(salesRes.data || []);
    }
  };

  // ===============================
  // ELIMINAR VENTA
  // ===============================
  const handleDeleteSale = async (orderId) => {
    try {
      const res = await fetch(`/api/delete_sale.php?id=${orderId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        setSales(prev => prev.filter(s => s.order_id !== orderId));
      } else {
        alert(data.message || "Error al eliminar");
      }
    } catch (error) {
      console.error("Error eliminando venta:", error);
    }
  };

  // ===============================
  // 🔥 DETERMINAR ACCESS DEL EMPLEADO
  // Administrador y Gerente ven todo (usan access del usuario sistema)
  // Otros roles solo ven pos y comandas
  // ===============================
  const getEffectiveAccess = () => {
    if (!employeeSession) return currentUser?.access;

    const adminRoles = ['Administrador', 'Gerente'];
    if (adminRoles.includes(employeeSession.role)) {
      return currentUser?.access; // acceso completo del sistema
    }

    // Acceso restringido para otros roles
    return {
      pos: true,
      comandas: true
    };
  };

  // ===============================
  // RENDERIZADO CON RUTAS
  // ===============================
  return (
    <Routes>
      {/* Login del sistema */}
      {!isAuthenticated ? (
        <Route path="*" element={<LoginForm onLogin={handleLogin} />} />
      ) : !employeeSession ? (
        // 🔥 Login de empleado POS (después del login del sistema)
        <Route
          path="*"
          element={
            <EmployeePOSLogin
              onEmployeeLogin={handleEmployeeLogin}
              onLogout={handleLogout}
            />
          }
        />
      ) : (
        // Dashboard con acceso según rol del empleado
        <Route
          path="/*"
          element={
            <Dashboard
              categories={categories}
              menuItems={menuItems}
              supplies={supplies}
              recipes={recipes}
              sales={sales}
              onDeleteSale={handleDeleteSale}
              supplyCategories={supplyCategories}
              employees={employees}
              inventory={inventory}
              onAddCategory={handleAddCategory}
              onDeleteCategory={handleDeleteCategory}
              onAddMenuItem={handleAddMenuItem}
              onUpdateMenuItem={handleUpdateMenuItem}
              onDeleteMenuItem={handleDeleteMenuItem}
              onAddSupply={handleAddSupply}
              onUpdateSupply={handleUpdateSupply}
              onDeleteSupply={handleDeleteSupply}
              onAddSupplyCategory={handleAddSupplyCategory}
              onDeleteSupplyCategory={handleDeleteSupplyCategory}
              onSaveRecipe={handleSaveRecipe}
              onUpdateRecipe={handleUpdateRecipe}
              onDeleteRecipe={handleDeleteRecipe}
              onAddEmployee={handleAddEmployee}
              onUpdateEmployee={handleUpdateEmployee}
              onDeleteEmployee={handleDeleteEmployee}
              onAddPOSSale={handleAddPOSSale}
              handleUpdateSaleStatus={handleUpdateSaleStatus}
              onLogout={handleLogout}
              onEmployeeLogout={handleEmployeeLogout}
              currentUser={currentUser}
              employeeSession={employeeSession}
              effectiveAccess={getEffectiveAccess()}
            />
          }
        />
      )}
    </Routes>
  );
};

export default App;